<template>
    <ModalDialogContainer ref="modalContainer" v-model:display="displayStatus">
        <div class="modal-dialog modal-lg" role="document" @click="stopPropagationEvent">
            <div class="modal-header">
                <div class="modal-title">
                    <router-link class="modal-link" to="#" @click="close" :title="$t('Go back')">
                        <i class="fa-solid fa-arrow-left-long"></i>
                        {{ $t("Go back") }}
                    </router-link>
                    <span v-if="loading">{{ $t("Loading DID document") }}</span>
                    <span v-else-if="notFound">{{ $t("DID document not found") }}</span>
                    <span v-else>{{ $t("DID Document") }}</span>
                </div>
                <button type="button" class="modal-close-btn" :title="$t('Close')" @click="close">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="modal-body">
                <ComponentLoader v-if="loading"></ComponentLoader>
                <CodeBlock :language="'json'" :code="didDocument" :name="$t('DID Document') + '(' + did + ')'"></CodeBlock>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "../../utils/v-model";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";
import { getUniqueStringId } from "@/utils/unique-id";
import ComponentLoader from "@/components/utils/ComponentLoader.vue";
import { renderDate } from "@/utils/time-utils";
import CodeBlock from "../utils/CodeBlock.vue";
import { ApiDataSpaceParticipants } from "@/api/api-group-data-space-participants";
import { prettifyJson } from "@/utils/objects";

export default defineComponent({
    components: {
        ComponentLoader,
        CodeBlock,
    },
    name: "DidDocumentModal",
    emits: ["update:display"],
    props: {
        display: Boolean,
        did: String,
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            loading: true,
            notFound: false,

            didDocument: "",
        };
    },
    methods: {
        close: function () {
            this.$closeModal();
        },

        escapeToClose: function (event) {
            if (event.key === "Escape") {
                this.close();
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        renderDate: function (date: string) {
            return renderDate(date, this.$t);
        },

        load: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);

            this.loading = true;

            Request.Pending(this.loadRequestId, ApiDataSpaceParticipants.ResolveDidDocument(this.did))
                .onSuccess((res) => {
                    this.loading = false;
                    this.notFound = !res.didDocument;
                    this.didDocument = prettifyJson(JSON.stringify(res.didDocument));
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequest: () => {
                            this.loading = false;
                            this.notFound = true;
                        },
                        temporalError: () => {
                            // Retry
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    // Retry
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },
    },
    mounted: function () {
        if (this.display) {
            this.load();
            this.$autoFocus();
        }
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
    },
    watch: {
        display: function () {
            if (this.display) {
                this.load();
                this.$autoFocus();
            }
        },
        uid: function () {
            this.load();
        },
    },
});
</script>

<style scoped>
.link {
    display: flex;
    align-items: center;
    gap: 6px;

    color: var(--color-text-primary-brand-default);

    font-size: var(--text-sm);
    font-weight: 600;

    text-decoration: none;
}

.modal-dialog {
    padding-bottom: 24px;
    gap: 20px;
}

.modal-body {
    padding-top: 0 !important;
}

.modal-title {
    padding-bottom: 0 !important;
    padding-top: 24px;
    gap: var(--space-2);
}
</style>
